import type {
  AppointmentDraftEditableResponse,
  AppointmentDraftNotEditableReason,
  AppointmentDraftResponse,
} from "../../../shared/api-types";
import { cleanSoapTextFromRichTextDocument } from "../../../shared/soap-draft-document";
import type { AppointmentDraftHeadObservation } from "./appointment-draft-autosave";

export type AppointmentDraftCopyOutcome =
  | { status: "copied" }
  | { status: "save_pending" }
  | { status: "stale"; current: AppointmentDraftEditableResponse }
  | { status: "not_editable"; reason: AppointmentDraftNotEditableReason }
  | { status: "unavailable" };

export interface AppointmentDraftClipboardWrite {
  writeText: (text: string) => Promise<void>;
  cancel: () => Promise<void>;
}

type ClipboardWriteResult =
  | { status: "written" }
  | { status: "failed"; error: unknown };

/**
 * Starts the browser write during the click's transient activation. The text
 * promise resolves only after the fresh server head passes validation.
 */
export function beginAppointmentDraftClipboardWrite(
  startWrite: (text: Promise<Blob>) => Promise<void> = (text) =>
    navigator.clipboard.write([new ClipboardItem({ "text/plain": text })])
): AppointmentDraftClipboardWrite {
  let resolveText!: (value: Blob) => void;
  let rejectText!: (error: Error) => void;
  let settled = false;
  const text = new Promise<Blob>((resolve, reject) => {
    resolveText = resolve;
    rejectText = reject;
  });
  const completion: Promise<ClipboardWriteResult> = startWrite(text).then(
    () => ({ status: "written" }),
    (error: unknown) => ({ status: "failed", error })
  );

  return {
    async writeText(value) {
      if (!settled) {
        settled = true;
        resolveText(new Blob([value], { type: "text/plain" }));
      }

      const result = await completion;
      if (result.status === "failed") throw result.error;
    },
    async cancel() {
      if (!settled) {
        settled = true;
        rejectText(new Error("appointment_draft_copy_cancelled"));
      }

      await completion;
    },
  };
}

export function appointmentDraftCopyFeedback(outcome: AppointmentDraftCopyOutcome): {
  kind: "success" | "error";
  message: string;
} {
  switch (outcome.status) {
    case "copied":
      return { kind: "success", message: "Note copied" };
    case "save_pending":
      return {
        kind: "error",
        message: "Wait for this draft to finish saving, then copy again.",
      };
    case "stale":
      return {
        kind: "error",
        message: "The draft changed. Review the latest version before copying.",
      };
    case "not_editable":
      return outcome.reason === "document_unsupported"
        ? {
            kind: "error",
            message:
              "This saved draft can't be copied because its format isn't supported.",
          }
        : { kind: "error", message: "This draft is no longer ready to copy." };
    case "unavailable":
      return { kind: "error", message: "Couldn't check the saved draft. Try again." };
  }
}

export async function copyAppointmentDraftAfterHeadCheck({
  readCurrent,
  observeCurrent,
  clipboard,
}: {
  readCurrent: () => Promise<AppointmentDraftResponse | null>;
  observeCurrent: (
    current: AppointmentDraftEditableResponse
  ) => AppointmentDraftHeadObservation;
  clipboard: AppointmentDraftClipboardWrite;
}): Promise<AppointmentDraftCopyOutcome> {
  try {
    const current = await readCurrent();

    if (current === null) {
      await clipboard.cancel();

      return { status: "unavailable" };
    }

    if (current.status === "not_editable") {
      await clipboard.cancel();

      return { status: "not_editable", reason: current.reason };
    }

    const observation = observeCurrent(current);

    if (observation !== "current") {
      await clipboard.cancel();
      if (observation === "save_pending") return { status: "save_pending" };

      if (observation === "unavailable") return { status: "unavailable" };

      return { status: "stale", current };
    }

    await clipboard.writeText(cleanSoapTextFromRichTextDocument(current.document));

    return { status: "copied" };
  } catch (error) {
    await clipboard.cancel();
    throw error;
  }
}

/**
 * NO-785: how the client reads an approval the server refused before writing
 * anything. A Re-enhance running in another tab gets its own outcome, so the
 * RD learns a new draft is being made instead of a generic "not yet".
 */
export function soapDraftApprovalRefusalOutcome(
  body: { code?: unknown; reason?: unknown } | undefined
): "refused" | "re_enhancing" | null {
  if (body?.code === "soap_draft_approval_not_ready") {
    return body.reason === "re_enhancing" ? "re_enhancing" : "refused";
  }

  return body?.code === "soap_draft_approval_refused" ? "refused" : null;
}
